// HEADER-DOCS-010: DeleteConfirmModal, shared confirm dialog.
// Reused by NotesModal, TodosModal and DocumentsTab per spec §5.2.
// Spec §5.5: destructive actions suppress backdrop-click dismissal.

import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

interface DeleteConfirmModalProps {
  itemName: string; // e.g. "this note", "this document", "Transcript.pdf"
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  confirmLabel?: string;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  itemName,
  onConfirm,
  onCancel,
  confirmLabel = 'Delete',
}) => {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleConfirm = async () => {
    setDeleting(true);
    setErr(null);
    try {
      await onConfirm();
      // Parent closes the modal on success.
    } catch (e) {
      setErr("We couldn't delete that. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={(_, reason) => {
        // §5.5: ignore backdrop clicks on destructive actions; Escape still cancels.
        if (reason !== 'backdropClick' && !deleting) onCancel();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Delete {itemName}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          This can be undone by a counselor from the admin tools, but it will disappear from view
          immediately.
        </Typography>
        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={deleting}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={deleting}>
          {deleting ? <CircularProgress size={16} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
